import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../rtkQuery';
import { restApiFormData } from '../axios';
import { API_ENDPOINTS } from '../constants';

export const positionsAPI = createApi({
    reducerPath: 'positionsAPI',
    baseQuery: axiosBaseQuery(restApiFormData),
    endpoints: (builder) => ({
        getPositions: builder.mutation({
            query: (query) => ({
                url: `${API_ENDPOINTS.LIST_POSITIONS}?${query.toString()}`,
                method: 'POST',
            }),
        }),
        searchPositions: builder.mutation({
            query: (query) => ({
                url: `${API_ENDPOINTS.SEARCH_POSITIONS}?${query.toString()}`,
                method: 'POST',
            }),
        }),
        getStatusByPositionCount: builder.mutation({
            query: () => ({
                url: API_ENDPOINTS.STATUS_BY_POSITION_COUNT,
                method: 'POST',
            }),
        }),
        preCreatePosition: builder.mutation({
            query: () => ({
                url: API_ENDPOINTS.PRE_CREATE_POSITION,
                method: 'POST',
            }),
        }),
        createPosition: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.CREATE_POSITION,
                method: 'POST',
                data,
            }),
        }),
        createPositionWithCompaFields: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.CREATE_POSITION_WITH_COMPA_FIELDS,
                method: 'POST',
                data,
            }),
        }),
        preModifyPosition: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_MODIFY_POSITION,
                method: 'POST',
                data,
            }),
        }),
        preModifyPositionWithCompaFields: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_MODIFY_POSITION_WITH_COMPA_FIELDS,
                method: 'POST',
                data,
            }),
        }),
        modifyPosition: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.MODIFY_POSITION,
                method: 'POST',
                data,
            }),
        }),
        modifyPositionWithCompaFields: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.MODIFY_POSITION_WITH_COMPA_FIELDS,
                method: 'POST',
                data,
            }),
        }),
        displayPosition: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.DISPLAY_POSITION,
                method: 'POST',
                data,
            }),
        }),
        bulkUpdatePositionStatuses: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.BULK_UPDATE_POSITION_STATUSES,
                method: 'POST',
                data,
            }),
        }),
        updatePositionsMappedToChangeRequestPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.UPDATE_POSITIONS_MAPPED_TO_CHANGE_REQUEST_POLICY,
                method: 'POST',
                data,
            }),
        }),
        updatePositionsMappedToRecruitmentPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.UPDATE_POSITIONS_MAPPED_TO_RECRUITMENT_POLICY,
                method: 'POST',
                data,
            }),
        }),
        resyncPosition: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.RESYNC_POSITION,
                method: 'POST',
                data,
            }),
        }),
        scheduleIntake: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.SCHEDULE_INTAKE,
                method: 'POST',
                data,
            }),
        }),
        completeIntake: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.COMPLETE_INTAKE,
                method: 'POST',
                data,
            }),
        }),
        updatePositionStartDate: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.UPDATE_POSITION_START_DATE,
                method: 'POST',
                data,
            }),
        }),
        positionStatusHistory: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.POSITION_STATUS_HISTORY,
                method: 'POST',
                data,
            }),
        }),
        loadPositionDataAction: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.LOAD_POSITION_DATA_ACTION,
                method: 'POST',
                data,
            }),
        }),
        updateExternalEntityId: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.UPDATE_EXTERNAL_ENTITY_ID,
                method: 'POST',
                data,
            }),
        }),
        positionDetails: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.POSITION_DETAILS,
                method: 'POST',
                data,
            }),
        }),
        positionDetailsWithCompaFields: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.POSITION_DETAILS_WITH_COMPA_FIELDS,
                method: 'POST',
                data,
            }),
        }),
        updateSinglePositionStatus: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.UPDATE_SINGLE_POSITION_STATUS,
                method: 'POST',
                data,
            }),
        }),
        bulkUpdatePositionStatus: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.BULK_UPDATE_POSITION_STATUSES,
                method: 'POST',
                data,
            }),
        }),
        exportPositionData: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.EXPORT_POSITION_DATA,
                method: 'POST',
                data,
            }),
        }),
        exportPositionDataWithCompaFields: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.EXPORT_POSITION_DATA_WITH_COMPA_FIELDS,
                method: 'POST',
                data,
            }),
        }),
        downloadPositionDataTemplate: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.DOWNLOAD_POSITION_DATA_TEMPLATE,
                method: 'POST',
                data,
            }),
        }),
        downloadPositionDataTemplateWithCompaFields: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.DOWNLOAD_POSITION_DATA_TEMPLATE_WITH_COMPA_FIELDS,
                method: 'POST',
                data,
            }),
        }),
        listUsers: builder.mutation({
            query: (query) => ({
                url: `${API_ENDPOINTS.LIST_USERS}?${query.toString()}`,
                method: 'POST',
            }),
        }),
    }),
});

export const {
    useGetPositionsMutation,
    useSearchPositionsMutation,
    useGetStatusByPositionCountMutation,
    usePreCreatePositionMutation,
    useCreatePositionMutation,
    useCreatePositionWithCompaFieldsMutation,
    usePreModifyPositionMutation,
    usePreModifyPositionWithCompaFieldsMutation,
    useModifyPositionMutation,
    useModifyPositionWithCompaFieldsMutation,
    useDisplayPositionMutation,
    useBulkUpdatePositionStatusesMutation,
    useUpdatePositionsMappedToChangeRequestPolicyMutation,
    useUpdatePositionsMappedToRecruitmentPolicyMutation,
    useResyncPositionMutation,
    useScheduleIntakeMutation,
    useCompleteIntakeMutation,
    useUpdatePositionStartDateMutation,
    usePositionStatusHistoryMutation,
    useLoadPositionDataActionMutation,
    useUpdateExternalEntityIdMutation,
    usePositionDetailsMutation,
    usePositionDetailsWithCompaFieldsMutation,
    useUpdateSinglePositionStatusMutation,
    useBulkUpdatePositionStatusMutation,
    useExportPositionDataMutation,
    useExportPositionDataWithCompaFieldsMutation,
    useDownloadPositionDataTemplateMutation,
    useDownloadPositionDataTemplateWithCompaFieldsMutation,
    useListUsersMutation,
} = positionsAPI;
